import { } from "@ant-design/icons";
import { Button, Drawer, Input, message, Radio, Spin, Tag } from "antd";
import { useEffect, useState } from "react";
import { fetchClientAddresses } from "../../../apis/client.api";
import { editPI, triggerGSTRetrofit } from "../../../apis/pro_forma_invoice.api";
import { getPurchaseOrders } from "../../../apis/po.api";
import AddClientAddressForm from "../../client/add_client_address_form";
import EditClientAddressForm from "../../client/edit_client_address_form";
import { isGstRetrofitStatus, arePosGstRetrofitEligible } from "../../../constants/gst.constants";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";

export interface PIEditAddressProps {
  estimate: any;
  type: "billing" | "shipping";
  refresh: () => void;
  onClose: () => void;
}

export function PIEditAddress({
  estimate,
  type,
  refresh,
  onClose,
}: PIEditAddressProps) {
  const { hasPermission } = usePermissions();
  const canEditAddress =
    hasPermission("deliverylocation.editall") ||
    hasPermission("deliverylocation.edit");
  const [clientAddresses, setClientAddresses] = useState<any[]>([]);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [showEditAddressForm, setShowEditAddressForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(
    type === "billing"
      ? estimate?.bill_address?.id ?? null
      : estimate?.ship_address?.id ?? null
  );

  const [posEligible, setPosEligible] = useState(true);


  const fetchAddresses = () => {
    setLoading(true);
    fetchClientAddresses(estimate?.client)
      .then((res) => {
        setClientAddresses(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!estimate?.client || !estimate?.id) {
      return;
    }

    fetchAddresses();
  }, [estimate?.id]);

  // An OMS-managed order has no legacy POs to be eligible or otherwise — its own guard runs
  // server-side (a filed IRN / e-way bill or a prior-month invoice is what refuses it there), so
  // fetching POs would only risk one stray legacy row blocking a conversion the backend allows.
  const isOmsManaged = !!estimate?.is_oms_pushed;

  useEffect(() => {
    if (!estimate?.id || isOmsManaged) {
      return;
    }
    getPurchaseOrders({
      estimate: estimate.id,
      page_size: 200,
      is_deleted: false,
      is_revised: false,
    }).then((res) => {
      setPosEligible(arePosGstRetrofitEligible(res?.results));
    });
  }, [estimate?.id, isOmsManaged]);

  const isActiveOrder = isGstRetrofitStatus(estimate?.estimate_status);
  const selectedAddress = clientAddresses.find((a) => a.id === selectedAddressId);
  const isRetrofitPath = type === "billing" && isActiveOrder;
  const isGstRemoval = isRetrofitPath && !selectedAddress?.gst_no && !!estimate?.client_gst;
  // A GST change is required (add or remove) but the order's POs aren't in an
  // allowed delivery state → block the edit instead of silently writing client_gst.
  const gstBlockedByPo =
    isRetrofitPath && (!!selectedAddress?.gst_no || isGstRemoval) && !posEligible;

  const editAddress = (force_update = false) => {
    if (!canEditAddress) {
      message.error("You don't have permission to edit this address");
      return;
    }
    if (!selectedAddressId) {
      return;
    }

    if (!selectedAddress) {
      message.warning({
        content: "Addresses are still loading. Please try again in a moment.",
        key: "edit-address",
      });
      return;
    }

    if (gstBlockedByPo) {
      message.warning({
        content:
          "Can't change GST: one or more POs are in a warehouse/return/refund state. " +
          "GST edits are allowed only when every PO is dispatch-pending, dispatched or delivered.",
        key: "edit-address",
      });
      return;
    }

    // GST retrofit path: billing address change on a post-order estimate.
    // - new address has gst_no  → B2B (Business customer)
    // - new address has no gst_no but estimate currently has client_gst → B2C switch
    // - no gst_no on either side → fall through to standard editPI (no Zoho work needed)
    const shouldRetrofit =
      isRetrofitPath && (!!selectedAddress?.gst_no || isGstRemoval);
    if (shouldRetrofit) {
      setSaving(true);
      message.loading({ content: "Initiating GST retrofit...", key: "edit-address" });
      triggerGSTRetrofit(
        estimate?.id,
        selectedAddressId,
        selectedAddress?.gst_no || "",
        selectedAddress?.company_name || selectedAddress?.name || ""
      ).then((res) => {
        if (!res) {
          message.error({
            content: "Failed to initiate GST retrofit. Please try again.",
            key: "edit-address",
          });
          return;
        }
        message.success({
          content: "GST retrofit started. Zoho documents will be updated in the background.",
          key: "edit-address",
          duration: 5,
        });
        refresh();
        onClose();
      }).finally(() => setSaving(false));
      return;
    }

    // Standard path: non-delivered or no GST
    const payload = {} as any;
    if (type === "billing") {
      payload.bill_address = selectedAddressId;
      payload.bill_address_description = `${selectedAddress?.flat_no}, ${selectedAddress?.address}`;
      payload.client_gst = selectedAddress?.gst_no || null;
    } else {
      payload.ship_address = selectedAddressId;
      payload.ship_address_description = `${selectedAddress?.flat_no}, ${selectedAddress?.address}`;
    }

    message.loading({ content: "Updating address...", key: "edit-address" });
    editPI(estimate?.id, payload, force_update).then((res) => {
      if (!res) {
        message.error({
          content: "Failed to update address. Please try again.",
          key: "edit-address",
        });
        return;
      }
      refresh();
      onClose();
      message.destroy("edit-address");
    });
  };

  return (
    <>
      <div>
        <div className="mb-2 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Editing {type} address</h5>
          <Button type="primary" className="me-2" loading={saving} disabled={saving || loading || !selectedAddress || gstBlockedByPo || !canEditAddress} onClick={() => editAddress()}>
            Save
          </Button>
          {!(isRetrofitPath && (selectedAddress?.gst_no || isGstRemoval)) && (
            <Button type="primary" disabled={saving || loading || !selectedAddress || !canEditAddress} onClick={() => editAddress(true)}>
              Force Save
            </Button>
          )}
        </div>
        {gstBlockedByPo && (
          <div className="alert alert-danger py-2 mb-2" style={{ fontSize: 12 }}>
            GST changes are <strong>blocked</strong> for this order: one or more POs are in a warehouse / return / refund / exchange state. Allowed only when every PO is <strong>dispatch-pending, dispatched or delivered</strong> (cancelled POs are ignored).
          </div>
        )}
        {isRetrofitPath && isOmsManaged && (!!selectedAddress?.gst_no || isGstRemoval) && (
          <div className="alert alert-info py-2 mb-2" style={{ fontSize: 12 }}>
            This order is <strong>managed in OMS</strong>. Saving re-bills its OMS sales order,
            invoices and delivery challans, then moves them to the matching Zoho customer and
            re-applies the payments. It is refused if an <strong>e-invoice (IRN)</strong> or{" "}
            <strong>e-way bill</strong> has been filed under the current GSTIN, or if any invoice
            predates this month. Track each document under <strong>Escalations</strong> on the OMS
            order page.
          </div>
        )}
        {isRetrofitPath && selectedAddress?.gst_no && (
          <div className="alert alert-warning py-2 mb-2" style={{ fontSize: 12 }}>
            The selected address has a <strong>GST number</strong>. Saving will validate the GSTIN with Zoho and rebind the order&apos;s Zoho sales documents to the business customer. Allowed only when every existing invoice on this order is from the <strong>current month</strong>.
          </div>
        )}
        {isGstRemoval && (
          <div className="alert alert-warning py-2 mb-2" style={{ fontSize: 12 }}>
            The new billing address has <strong>no GST number</strong>. Saving will clear the GST on this estimate and rebind Zoho sales documents to the individual (B2C) customer. Allowed only when every existing invoice on this order is from the <strong>current month</strong>.
          </div>
        )}
        {loading && (
          <div className="d-flex justify-content-center mb-3">
            <Spin />
          </div>
        )}
        {!loading && (
          <>
            <Button
              className="w-100 d-block mb-2"
              type="default"
              onClick={() => setShowAddAddressForm(true)}
            >
              Add Address
            </Button>
            <Input.Search
              placeholder="Search addresses..."
              allowClear
              className="mb-2"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </>
        )}
        {[...clientAddresses].reverse().filter((address) => {
          if (type === "shipping" && address?.is_billing) {
            return false;
          }
          const q = searchQuery.toLowerCase();
          return (
            !q ||
            address?.name?.toLowerCase().includes(q) ||
            address?.address?.toLowerCase().includes(q) ||
            address?.city?.toLowerCase().includes(q) ||
            address?.pincode?.toString().includes(q) ||
            // A client with several GST entities is picked apart by GSTIN, not by street —
            // matching it here is how you find "the address that bills under THIS number".
            address?.gst_no?.toLowerCase().includes(q)
          );
        }).map((address) => (
          <div
            key={address?.id}
            className="rounded border shadow-sm mb-2 d-flex gap-1 align-items-center p-2"
            role="button"
            onClick={() => {
              setSelectedAddressId(address?.id);
            }}
          >
            <div>
              <Radio checked={address?.id === selectedAddressId} />
            </div>
            <div className="flex-grow-1">
              {address?.name}
              <div className="text-muted" style={{ fontSize: 12 }}>
                {address?.address}
              </div>
              {/* The GST identity each address carries. Shown on every row because picking one
                  here is what SETS the order's identity: the standard path writes
                  `client_gst = selectedAddress.gst_no`, and the retrofit path re-bills every Zoho
                  document under it. Without this the operator is choosing a tax entity blind —
                  two addresses for the same client can differ only by GSTIN. "No GST" is stated
                  rather than left blank so an empty row reads as B2C, not as missing data. */}
              {type === "billing" && (
                <div style={{ fontSize: 12, marginTop: 2 }}>
                  {address?.gst_no ? (
                    <>
                      <Tag color="blue" style={{ marginRight: 4 }}>B2B</Tag>
                      <span style={{ fontFamily: "monospace" }}>{address.gst_no}</span>
                    </>
                  ) : (
                    <Tag>B2C · no GST</Tag>
                  )}
                </div>
              )}
            </div>
            <div>
              {/* <Button
                type="default"
                size="small"
                onClick={(ev) => {
                  setShowEditAddressForm(true);
                }}
              >
                Edit
              </Button> */}
            </div>
          </div>
        ))}
      </div>
      <Drawer
        open={showEditAddressForm}
        onClose={() => {
          setShowEditAddressForm(false);
        }}
        title="Edit Address"
      >
        <EditClientAddressForm
          address={clientAddresses.find((a) => a.id === selectedAddressId)}
          fetchAddresses={fetchAddresses}
          onClientEditAddressClose={() => {
            setShowEditAddressForm(false);
          }}
        />
      </Drawer>
      <Drawer
        open={showAddAddressForm}
        onClose={() => setShowAddAddressForm(false)}
        title="Add Address"
      >
        <AddClientAddressForm
          fetchAddresses={fetchAddresses}
          onClientAddAddressDrawerClose={() => {
            setShowAddAddressForm(false);
          }}
          user_id={estimate?.client}
          billingOnly={type === "billing"}
          shippingOnly={type === "shipping"}
        />
      </Drawer>
    </>
  );
}
